import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, ExternalLink, Loader2, MinusCircle } from 'lucide-react';
import type { BlockEnvelope, BlockKey } from '@foco/contracts';
import { formatDateTime } from '../../shared/lib/format';
import { RegistrationBlock } from './blocks/RegistrationBlock';
import { AddressBlock } from './blocks/AddressBlock';
import { ActivityBlock } from './blocks/ActivityBlock';
import { FoundersBlock } from './blocks/FoundersBlock';
import { ProcurementBlock } from './blocks/ProcurementBlock';
import { RiskRegistryBlock } from './blocks/RiskRegistryBlock';
import { DebtBlock } from './blocks/DebtBlock';
import { GenericBlock } from './blocks/GenericBlock';
import { MaskedBlock } from './blocks/MaskedBlock';

/**
 * One block of the card.
 *
 * Three states have to look clearly different, because confusing them is the
 * worst failure this product can have:
 *
 *   * **ready with data** — the registry answered;
 *   * **ready but empty** — the registry answered and there is nothing on
 *     record. This is a *finding*, not a gap, and it is rendered as such: for
 *     sanctions and debarment lists "not found" is a meaningful, reassuring
 *     answer (section 3.5);
 *   * **unavailable / timeout / skipped** — we could not ask, or there is no
 *     source for this country. This must never be readable as "clean", which is
 *     why it is amber rather than green and says explicitly that the source did
 *     not respond.
 */

interface BlockCardProps {
  readonly blockKey: BlockKey;
  readonly block?: BlockEnvelope;
  readonly onRetry: () => void;
  readonly retrying: boolean;
  /** Public company pages and the free tier render masked blocks. */
  readonly masked?: boolean;
}

/** Blocks that carry meaning when empty, so "not found" is a real answer. */
const EMPTINESS_IS_AN_ANSWER: ReadonlySet<BlockKey> = new Set([
  'risk_registry',
  'procurement',
  'debt',
  'founders',
  'management',
]);

export function BlockCard({
  blockKey,
  block,
  onRetry,
  retrying,
  masked = false,
}: BlockCardProps): React.ReactNode {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const state = block?.state ?? 'pending';
  const label = t(`blocks.${blockKey}.label`, blockKey);
  const description = t(`blocks.${blockKey}.description`, '');

  const sourceList = block?.sources ?? [];

  return (
    <section
      aria-labelledby={`block-${blockKey}`}
      aria-busy={state === 'pending'}
      className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--shadow-card)]"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={`block-${blockKey}`} className="font-semibold">
            {label}
          </h2>
          {description && (
            <p className="mt-0.5 text-xs text-[var(--color-muted-fg)]">{description}</p>
          )}
        </div>

        <StateIcon state={state} />
      </header>

      {/* Filterable print stylesheet: the card prints without the controls. */}
      <div className="mt-4">
        {state === 'pending' && <Skeleton />}

        {state === 'ready' &&
          (masked ? (
            <MaskedBlock blockKey={blockKey} data={block?.data} />
          ) : (
            <BlockBody blockKey={blockKey} block={block as BlockEnvelope} />
          ))}

        {(state === 'unavailable' || state === 'timeout' || state === 'skipped') && (
          <div className="rounded-[var(--radius-control)] border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950">
            <p className="font-medium text-amber-900 dark:text-amber-100">
              {state === 'skipped' ? t('lookup.sourceSkipped') : t('lookup.sourceDidNotRespond')}
            </p>
            {block?.error && (
              <p className="mt-1 text-amber-800 dark:text-amber-200">
                {/* The server sends a stable code (`block_timeout`,
                    `geo_blocked`, …); the sentence is ours, so it is in the
                    language of the page. An unknown code falls back to the
                    server's English text rather than showing a raw key. */}
                {t(`lookup_errors.${block.error.code}`, {
                  defaultValue: block.error.message,
                })}
              </p>
            )}
            {state !== 'skipped' && (
              <button
                type="button"
                onClick={onRetry}
                disabled={retrying}
                className="mt-2 inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border border-amber-400 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60 dark:text-amber-100 dark:hover:bg-amber-900"
              >
                {retrying && <Loader2 aria-hidden className="h-3 w-3 animate-spin" />}
                {retrying ? t('lookup.retrying') : t('lookup.retryBlock')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Provenance. Section 2.3 makes this mandatory next to every fact. */}
      {sourceList.length > 0 && (
        <footer className="mt-4 border-t border-[var(--color-border)] pt-3">
          <p className="text-xs text-[var(--color-muted-fg)]">
            <span className="font-medium">{t('lookup.sourcesTitle')}:</span>{' '}
            {sourceList.map((source, index) => (
              <span key={`${source.code}-${index}`}>
                {index > 0 && ', '}
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-0.5 hover:text-brand-600 hover:underline"
                  >
                    {source.name}
                    <ExternalLink aria-hidden className="h-3 w-3" />
                  </a>
                ) : (
                  source.name
                )}
              </span>
            ))}
            {block?.latencyMs != null && (
              <span className="ml-2 opacity-70">
                {formatDateTime(sourceList[0].fetchedAt, locale)} · {block.latencyMs} ms
              </span>
            )}
          </p>
        </footer>
      )}
    </section>
  );
}

/** The per-block spinner: a few pulsing lines, not a full-page overlay. */
function Skeleton(): React.ReactNode {
  return (
    <div className="space-y-2" aria-hidden>
      <div className="h-3 w-3/4 animate-pulse rounded bg-[var(--color-surface-2)]" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-[var(--color-surface-2)]" />
      <div className="h-3 w-2/3 animate-pulse rounded bg-[var(--color-surface-2)]" />
    </div>
  );
}

function StateIcon({ state }: { state: BlockEnvelope['state'] }): React.ReactNode {
  if (state === 'pending') {
    return <Loader2 aria-hidden className="h-4 w-4 shrink-0 animate-spin text-[var(--color-muted-fg)]" />;
  }
  if (state === 'ready') {
    return <CheckCircle2 aria-hidden className="h-4 w-4 shrink-0 text-risk-low" />;
  }
  if (state === 'skipped') {
    return <MinusCircle aria-hidden className="h-4 w-4 shrink-0 text-[var(--color-muted-fg)]" />;
  }
  return <AlertCircle aria-hidden className="h-4 w-4 shrink-0 text-risk-medium" />;
}

/** Route a ready block to its renderer. */
function BlockBody({
  blockKey,
  block,
}: {
  blockKey: BlockKey;
  block: BlockEnvelope;
}): React.ReactNode {
  const { t } = useTranslation();
  const data = block.data;

  const isEmpty =
    data === null ||
    data === undefined ||
    (Array.isArray(data) && data.length === 0) ||
    (typeof data === 'object' &&
      data !== null &&
      'total' in data &&
      (data as { total?: number }).total === 0);

  if (isEmpty) {
    // "Nothing on record" is a finding for a risk list and a gap for a contact
    // block; the wording differs, so the distinction is made explicitly.
    return (
      <p className="text-sm text-[var(--color-muted-fg)]">
        {EMPTINESS_IS_AN_ANSWER.has(blockKey)
          ? t(`blocks.${blockKey}.empty`, t('blocks.emptyNoted'))
          : t(`blocks.${blockKey}.empty`, t('blocks.emptyNoted'))}
      </p>
    );
  }

  switch (blockKey) {
    case 'registration':
      return <RegistrationBlock data={data} />;
    case 'address':
      return <AddressBlock data={data} />;
    case 'activity':
      return <ActivityBlock data={data} />;
    case 'founders':
    case 'management':
      return <FoundersBlock data={data} />;
    case 'procurement':
      return <ProcurementBlock data={data} />;
    case 'risk_registry':
      return <RiskRegistryBlock data={data} />;
    case 'debt':
      return <DebtBlock data={data} />;
    default:
      return <GenericBlock data={data} />;
  }
}
